import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import Overlay from "@/components/Overlay";
import { DESTINATIONS } from "@/data/content";

function Card({ d, onOpen, className = "", tall = false }) {
  return (
    <button
      data-testid={`destination-card-${d.slug}`}
      onClick={() => onOpen(d)}
      className={`group relative block w-full overflow-hidden text-left ${className}`}
    >
      <img
        src={d.image}
        alt={d.name}
        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-forest/85 via-forest/10 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
        <p className="text-[10px] uppercase tracking-widest2 text-gold">{d.tag}</p>
        <div className="mt-2 flex items-end justify-between gap-4">
          <h3 className="font-display text-3xl text-ivory md:text-4xl">{d.name}</h3>
          <span className="mb-1 flex items-center gap-2 text-[10px] uppercase tracking-widest2 text-gold opacity-0 transition-all duration-500 group-hover:opacity-100 md:translate-x-2 md:group-hover:translate-x-0">
            Explore <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
        <p className={`gold-link mt-3 max-w-md text-xs leading-relaxed text-ivory/80 md:text-sm ${tall ? "hidden md:block" : "hidden lg:block"}`}>
          {d.blurb}
        </p>
      </div>
    </button>
  );
}

export default function Destinations({ onPlanJourney }) {
  const [active, setActive] = useState(null);
  const [headingHeight] = useState(0);

  const open = (d) => setActive(d);
  const close = () => setActive(null);

  return (
    <section id="destinations" data-testid="destinations-section" className="bg-ivory py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <Reveal>
              <Eyebrow>Destinations</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 max-w-xl text-4xl leading-[1.15] text-forest md:text-5xl">
                Places we know <span className="italic text-gold">intimately.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <p className="max-w-sm text-sm leading-relaxed text-forest-mist">
              Eight corners of the world, curated with the same care: the right season, the right stay, the right pace.
              Choose a destination to see how we design it.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-12 md:gap-5">
          <Reveal className="md:col-span-7" y={40}>
            <Card d={DESTINATIONS[0]} onOpen={open} tall className="h-[300px] md:h-[540px]" />
          </Reveal>
          <Reveal className="md:col-span-5" y={40} delay={0.08}>
            <Card d={DESTINATIONS[1]} onOpen={open} tall className="h-[300px] md:h-[540px]" />
          </Reveal>
          <Reveal className="md:col-span-5" y={40}>
            <Card d={DESTINATIONS[2]} onOpen={open} tall className="h-[300px] md:h-[460px]" />
          </Reveal>
          <Reveal className="md:col-span-7" y={40} delay={0.08}>
            <Card d={DESTINATIONS[3]} onOpen={open} tall className="h-[300px] md:h-[460px]" />
          </Reveal>
          {DESTINATIONS.slice(4, 7).map((d, i) => (
            <Reveal key={d.slug} className="md:col-span-4" y={40} delay={i * 0.08}>
              <Card d={d} onOpen={open} className="h-[300px] md:h-[420px]" />
            </Reveal>
          ))}
          <Reveal className="md:col-span-12" y={40}>
            <Card d={DESTINATIONS[7]} onOpen={open} className="h-[300px] md:h-[340px]" />
          </Reveal>
        </div>
      </div>

      <Overlay
        open={!!active}
        onClose={close}
        image={active?.image}
        imageAlt={active?.name}
        label={active?.tag}
      >
        {active && (
          <div data-testid="destination-overlay-content">
            <p className="text-[10px] uppercase tracking-widest2 text-gold">{active.tag}</p>
            <h3 className="mt-3 font-display text-4xl text-forest md:text-5xl">{active.name}</h3>
            <p className="mt-6 text-[15px] leading-relaxed text-forest-mist">{active.intro}</p>

            <p className="mt-8 text-[10px] uppercase tracking-widest2 font-medium text-forest">Signature Experiences</p>
            <ul className="mt-4 space-y-3">
              {active.highlights.map((h) => (
                <li key={h} className="flex items-start gap-3 text-sm text-forest-mist">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" aria-hidden="true" />
                  {h}
                </li>
              ))}
            </ul>

            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="border-t border-gold/40 pt-4">
                <p className="text-[10px] uppercase tracking-widest2 text-gold">When to Go</p>
                <p className="mt-2 text-sm leading-relaxed text-forest-mist">{active.when}</p>
              </div>
              <div className="border-t border-gold/40 pt-4">
                <p className="text-[10px] uppercase tracking-widest2 text-gold">Journey Style</p>
                <p className="mt-2 text-sm leading-relaxed text-forest-mist">{active.style}</p>
              </div>
            </div>

            <button
              data-testid={`plan-journey-from-${active.slug}`}
              onClick={() => {
                const dest = active.name;
                close();
                setTimeout(() => onPlanJourney(dest), 200);
              }}
              className="mt-10 w-full bg-forest px-8 py-4 text-[11px] uppercase tracking-widest2 font-medium text-ivory transition-colors duration-500 hover:bg-gold hover:text-forest sm:w-auto"
            >
              Plan This Journey
            </button>
          </div>
        )}
      </Overlay>
    </section>
  );
}
